import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import { useAuth } from "../../context/Auth";
import DebouncedSearchInput from "../../components/common/DebouncedSearchInput";
import RoleBasedComponent from "../../components/RoleBasedComponent";
import ExportData from "../../components/common/ExportData";
import Swal from "sweetalert2";
import { useGlobalContext } from "../../context/stateContext";

const PriceUpdates = () => {
  const { activeSession, currentUser, getCurrentUser, checkLastInventoryUpdateForUser } = useGlobalContext();
  const { requestWithAuth } = useAuth();
  const [priceUpdates, setPriceUpdates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [downloadingInventory, setDownloadingInventory] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);

  const handleAcknowledgeUpdates = async () => {
    setAcknowledging(true);
    try {
      await requestWithAuth(`/users/last-inventory-downloaded/`, "POST");
      window.location.reload()
      // toast.success("Price updates acknowledged successfully");
    } catch (error) {
      toast.error("Failed to acknowledge updates");
      console.error("Error updating inventory last downloaded:", error);
    } finally {
      setAcknowledging(false);
    }
  };

  const fetchPriceUpdates = async (page_size, search_query, current_page) => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/inventory/price-updates/?page=${current_page}&pageSize=${page_size}&search=${search_query}`,
        "GET"
      );
      setPriceUpdates(data?.results);
      setNextPage(data?.next);
      setPrevPage(data?.prev);
      setTotalPages(Math.ceil(data?.count / pageSize));
    } catch (error) {

      toast.error("Failed to fetch price updates");
    } finally {
      setLoading(false);
    }
  };

  const getTableData = async () => {
    try {
      const data = await requestWithAuth(
        `/inventory/price-updates/?paginated=false&search=${search}`,
        "GET"
      );
      setTableData(data);
    } catch (error) {
      console.error("Error fetching price updates data:", error);
    }
  };

  const downloadInventoryList = async () => {
    setDownloadingInventory(true);
    try {
      const data = await requestWithAuth(
        `/inventory/download/branch-inventory/${activeSession?.branch?.id}/`,
        "GET"
      );

      // Convert to CSV format
      const branchName = activeSession?.branch?.name || "All Branches";
      const downloadDate = new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });

      const headers = ["Drug", "Selling Price", "Quantity Left"];
      const csvContent = [
        `"Inventory List - ${branchName}"`,
        `"Downloaded on: ${downloadDate}"`,
        "",
        headers.join(","),
        ...data.map((item) =>
          [`"${item?.drug_name?.replace(/"/g, '""')}"`, item.selling_price, item.quantity].join(",")
        ),
      ].join("\n");

      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `inventory_list.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      await requestWithAuth(`/users/last-inventory-downloaded/`, "POST");
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Inventory list downloaded successfully",
      }).then(() => {
        window.location.reload();
      });
    } catch (error) {
      toast.error("Failed to download inventory list");
    } finally {
      setDownloadingInventory(false);
    }
  };

  const filterPriceUpdatesList = (search_query) => {
    setSearch(search_query);
    setCurrentPage(1);
  };

  const handleNextPage = () => {
    if (nextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (prevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3;

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, idx) => (
      <button
        key={idx}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`min-w-[36px] h-[36px] flex items-center justify-center rounded-lg text-sm font-medium transition-all
          ${number === currentPage
            ? "bg-blue-500 text-white shadow-sm"
            : number === "..."
              ? "cursor-default text-gray-400"
              : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        {number}
      </button>
    ));
  };

  const calculatePriceChange = (oldPrice, newPrice) => {
    if (!oldPrice || oldPrice === 0) return 0;
    return (((newPrice - oldPrice) / oldPrice) * 100).toFixed(2);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    fetchPriceUpdates(pageSize, search, currentPage);
    getTableData();
  }, [search, currentPage, pageSize]);



  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Drugs</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">Price Updates</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900">
                Price Update History
              </h1>
            </div>
            <div className="flex items-center gap-4">
              {currentUser?.role === "superadmin" && (
                <button
                  onClick={handleAcknowledgeUpdates}
                  disabled={acknowledging}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2 disabled:opacity-50"
                >
                  {acknowledging ? "Acknowledging..." : "Acknowledge Updates"}
                </button>
              )}
              <button
                onClick={downloadInventoryList}
                disabled={downloadingInventory}
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2 disabled:opacity-50"
              >
                {downloadingInventory ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download size={20} />
                    Download Inventory List
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Filters Bar */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <ExportData
                  tableData={tableData}
                  fileName="Price Updates History"
                />
                <span className="text-sm text-gray-600">Show</span>
                <select
                  onChange={(e) => setPageSize(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  value={pageSize}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span className="text-sm text-gray-600">entries</span>
              </div>

              <DebouncedSearchInput
                onSearch={filterPriceUpdatesList}
                placeholder="Search price updates..."
                delay={500}
                className="flex-1 max-w-md"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span>Loading price updates...</span>
                </div>
              </div>
            ) : priceUpdates?.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Drug ID
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Drug Name
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Old Price
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      New Price
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Change
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Updated By
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Date Updated
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {priceUpdates?.map((update, index) => {
                    const priceChange = calculatePriceChange(
                      update?.old_cash_price,
                      update?.new_cash_price
                    );
                    const isIncrease = priceChange > 0;

                    return (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="py-4 px-6 text-sm text-gray-900">
                          {update?.drug_id}
                        </td>
                        <td className="py-4 px-6 text-sm font-medium text-gray-900">
                          {update?.drug_name}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600">
                          GH₵ {parseFloat(update?.old_cash_price).toFixed(2)}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-900 font-medium">
                          GH₵ {parseFloat(update?.new_cash_price).toFixed(2)}
                        </td>
                        <td className="py-4 px-6 text-sm">
                          <div
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${isIncrease
                              ? "bg-green-50 text-green-700"
                              : priceChange < 0
                                ? "bg-red-50 text-red-700"
                                : "bg-gray-50 text-gray-700"
                              }`}
                          >
                            {isIncrease ? (
                              <TrendingUp size={14} />
                            ) : priceChange < 0 ? (
                              <TrendingDown size={14} />
                            ) : null}
                            {priceChange > 0 ? "+" : ""}
                            {parseFloat(
                              update?.cash_price_change_percentage
                            ).toFixed(2)}
                            %
                          </div>
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600">
                          {update?.updated_by_name || "System"}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600">
                          {formatDate(update?.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="py-20 text-center">
                <div className="text-gray-400 mb-2">
                  <svg
                    className="mx-auto h-12 w-12"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <p className="text-gray-500 text-sm">No price updates found</p>
                {search && (
                  <p className="text-gray-400 text-xs mt-1">
                    Try adjusting your search terms
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Pagination */}
          {priceUpdates?.length > 0 && (
            <div className="p-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing page {currentPage} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg transition-all ${currentPage > 1
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div className="flex items-center gap-1">
                    {renderPageNumbers()}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg transition-all ${currentPage < totalPages
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PriceUpdates;
